import { GenericStatusLabel } from '../../../../shared/components/GenericStatusLabel/GenericStatusLabel';
import { OBJECT_CONFIG_STATUS_DATA_MAP } from '../../constants/object-config-status';
import { ObjectCell } from './ObjectCell';
import { TableColumn } from '../../../../table/interfaces/table';
import { ValidObjectsHeaders } from '../../interfaces/object-header';
import { ObjectConfig } from '../../interfaces/object';
import { ObjectHeaderCell } from './ObjectHeaderCell';
import { ObjectConfigActionsCell } from './ObjectConfigActionsCell/ObjectConfigActionsCell';
import { shouldRenderObjectConfigCell } from '../../utils';

export interface ObjectsConfigTableGlobalContext {
  pipelineId: number;
  isViewMode?: boolean;
  selected?: number;
  total?: number;
  isListIncluded?: boolean;
  isListIndeterminate: boolean;
  changeCheckedState: (objectID: string, included: boolean) => void;
  changeSelectAllCheckedState: (included: boolean) => void;
  setFieldsSelectionModalConfig: (
    params: { visible: boolean; object?: ObjectConfig },
    saveSelection?: boolean
  ) => void;
  onObjectAction: (updatedObject: ObjectConfig) => void;
}

export function getObjectsConfigColumns(headers: ValidObjectsHeaders, isViewMode: boolean) {
  const headerEntries = Object.entries(headers);

  let columns: TableColumn<ObjectConfig, ObjectsConfigTableGlobalContext>[] = headerEntries.map(
    ([key, header], index) => {
      let width = 150;
      let isFlexible = true;

      if (index === 0) {
        isFlexible = true;
      }

      if (key === 'fieldCount') {
        width = 54;
        isFlexible = false;
      }

      if (key === 'loadMode') {
        width = 100;
        isFlexible = false;
      }

      if (key === 'keys') {
        width = 160;
        isFlexible = false;
      }

      return {
        id: key,
        field: key,
        headerName: '',
        width,
        isFlexible,
        shouldRenderCell: ({ row }) => shouldRenderObjectConfigCell(row.data, key),
        renderHeaderCell: ({ globalContext }) => {
          const {
            selected,
            total,
            isListIncluded,
            isListIndeterminate,
            changeSelectAllCheckedState
          } = globalContext as ObjectsConfigTableGlobalContext;

          return (
            <ObjectHeaderCell
              index={index}
              header={header}
              selected={selected}
              total={total}
              isViewMode={isViewMode}
              isListIncluded={isListIncluded}
              isListIndeterminate={isListIndeterminate}
              changeSelectAllCheckedState={changeSelectAllCheckedState}
            />
          );
        },
        renderCell: ({ row, globalContext }) => (
          <ObjectCell
            index={index}
            onDefineKeysClick={() => {
              (globalContext as ObjectsConfigTableGlobalContext).setFieldsSelectionModalConfig({
                visible: true,
                object: row.data
              });
            }}
            isViewMode={isViewMode}
            objectRow={row.data}
            cellKey={key}
            cellHeader={header}
            onObjectSelectionChange={included => {
              (globalContext as ObjectsConfigTableGlobalContext).changeCheckedState(
                row.data.id,
                included
              );
            }}
          />
        )
      };
    }
  );

  columns = [
    ...columns,
    {
      id: 'c1',
      field: 'actions',
      headerName: 'Actions',
      width: isViewMode ? 125 : 52,
      justifyEnd: true,
      getColSpan: ({ row }) =>
        !row.data.isConfigurable ? [columns.length - 4, columns.length - 1] : undefined,
      renderCell: ({ row, globalContext: g }) => {
        const globalContext = g as ObjectsConfigTableGlobalContext;

        return (
          <ObjectConfigActionsCell
            pipelineId={globalContext.pipelineId}
            isViewMode={isViewMode}
            object={row.data}
            onObjectAction={globalContext.onObjectAction}
            openFieldsSelectionModal={object => {
              globalContext.setFieldsSelectionModalConfig(
                {
                  visible: true,
                  object
                },
                true
              );
            }}
          />
        );
      }
    }
  ];

  if (isViewMode) {
    columns.splice(1, 0, {
      id: 'status',
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: ({ row }) => {
        return <GenericStatusLabel status={OBJECT_CONFIG_STATUS_DATA_MAP[row.data.status]} />;
      }
    });
  }

  return columns;
}
